/**
 * How a bot picks an action.
 *
 * Every legal action is priced in chips, the prices are compared, and the profile
 * decides how reliably the best one is taken. That structure is deliberate: it means a
 * weak bot and a strong bot run the SAME model and differ in how well they use it,
 * which is what makes the difficulty dial mean something, and it means every decision
 * comes with a number the leak report can quote back at the player.
 *
 * The prices are estimates from a model, not solves. The model is:
 *
 *   fold      0, because chips already in the pot are not ours to lose again
 *   call      equity × (pot + call) − call, against the range that would still be
 *             there, with an implied-odds adjustment for draws and position
 *   bet/raise foldEquity × pot  +  (1 − foldEquity) × [ equity-when-called × (pot +
 *             2 × bet) − bet ]
 *
 * The two places this is honest about being approximate: `foldEquity` is derived from
 * minimum-defence frequency adjusted by what the opponent has actually been doing, and
 * "equity when called" is measured against the top of their range rather than all of
 * it — which is the difference between a bot that value-bets and one that bets into
 * hands that only ever call with better.
 *
 * Nothing here is a solve and nothing derived from it is labelled GTO.
 */
import type { Hand } from "../hand";
import type { Action, Card, Street } from "../types";
import { classify } from "../handclass";
import { boardStrength } from "./strength";
import { texture, rangeAdvantage } from "../texture";
import { equityVsRanges } from "../equity";
import { Range, comboCards, comboId } from "../range";
import { handRank } from "./ranking";
import { comboCode } from "../range";
import { rangeCodes, openPercent, defenceAgainstRaise } from "./preflop";
import { foldToBet, perceived, type SeatHistory, type SeatRead } from "./reads";
import type { BotProfile } from "./profile";

export interface PricedAction {
  action: Action;
  /** Expected chips, relative to folding. Folding is always exactly zero. */
  ev: number;
  /** What to call this in a hand history. */
  label: string;
  /** One clause of explanation, for the replay. */
  note: string;
}

export interface Decision {
  action: Action;
  /** Every legal action with its price, best first. Drives the leak report. */
  priced: PricedAction[];
  /** The action the model rated best, which may not be the one taken. */
  best: PricedAction;
  /** Chips given up by not taking the best action. Zero when the best was taken. */
  evLost: number;
  why: string;
}

export interface PolicyInput {
  hand: Hand;
  seat: number;
  profile: BotProfile;
  /** Beliefs about every other seat. Missing entries are treated as unread. */
  reads: Map<number, SeatRead>;
  /** Long-run observations per seat, indexed by seat. Optional: a fresh table has none. */
  histories?: SeatHistory[];
  rng: () => number;
  /** Monte Carlo trials per equity question. Lower it for speed on big tables. */
  trials?: number;
}

const CAND_FRACTIONS = [0.33, 0.5, 0.75, 1.25] as const;

export function decide(input: PolicyInput): Decision {
  const { hand, seat, profile, rng } = input;
  const legal = hand.legalActions(seat);
  if (legal.length === 0) throw new Error(`seat ${seat} has no legal actions`);

  const priced = hand.street === "preflop"
    ? pricePreflop(input, legal)
    : pricePostflop(input, legal);

  /**
   * One entry per distinct move. A duplicate would get double weight in the softmax
   * below and would let grading pick whichever copy flattered the player.
   */
  const seen = new Set<string>();
  const unique: PricedAction[] = [];
  for (const p of priced) {
    const key = `${p.action.kind}:${p.action.to ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(p);
  }
  unique.sort((a, b) => b.ev - a.ev);
  const priced2 = unique;
  const best = priced2[0]!;

  /**
   * Which action is actually taken.
   *
   * Not "best with probability p, uniform otherwise": a weak player's mistakes are
   * plausible, not random. They call when they should fold, they do not fold when the
   * pot is laying them 20:1. So the alternatives are weighted by how close they were,
   * and how sharply, by discipline.
   */
  const chosen = pickAction(priced2, profile, rng, hand.rules.bigBlind);
  const evLost = Math.max(0, best.ev - chosen.ev);

  return {
    action: chosen.action, priced: priced2, best, evLost,
    why: chosen === best ? chosen.note
      : `${chosen.note} (the model preferred ${best.label}, worth ${(best.ev - chosen.ev).toFixed(2)} more)`,
  };
}

function pickAction(
  priced: PricedAction[], profile: BotProfile, rng: () => number, unit: number
): PricedAction {
  const best = priced[0]!;
  if (priced.length === 1) return best;

  /**
   * Volatility: a rare, deliberately out-of-character move. Real players spew, get
   * curious, and tilt, and a table where nobody ever does is a table you cannot
   * practise against. Kept rare, never the single worst option available, and always
   * logged as what it is.
   */
  if (profile.style.volatility > 0 && rng() < profile.style.volatility) {
    const wild = priced.filter((p) => p.action.kind !== "fold").slice(0, -1);
    if (wild.length > 0) {
      const pick = wild[Math.floor(rng() * wild.length) % wild.length]!;
      return { ...pick, note: `${pick.note} — out of character` };
    }
  }

  /**
   * A softmax over EVERY action, including the best one.
   *
   * The first version was "take the best with probability `discipline`, otherwise pick
   * among the rest" — and that second clause is a trap. It meant a player with
   * discipline 0.4 took a non-best action six times in ten NO MATTER HOW BAD it was,
   * because the best action was excluded from the draw. A calling station was calling
   * hundred-blind shoves with 94s several times an hour, which is not a calling
   * station, it is a random number generator with a seat.
   *
   * Including the best action in the draw makes `discipline` a temperature rather than
   * a coin flip: a weak player still mostly does something reasonable, and their
   * mistakes are the close ones. `scale` is in BIG BLINDS, so the same profile plays
   * the same way at every stake.
   */
  const scale = unit * (0.06 + Math.pow(1 - profile.discipline, 1.5) * 2.2);
  const weights = priced.map((p) => Math.exp(-(best.ev - p.ev) / scale));
  const total = weights.reduce((a, b) => a + b, 0);
  if (!(total > 0) || !Number.isFinite(total)) return best;
  let u = rng() * total;
  for (let i = 0; i < priced.length; i++) {
    u -= weights[i]!;
    if (u <= 0) return priced[i]!;
  }
  return best;
}

/* ------------------------------------------------------------------ preflop --- */

/**
 * Preflop is priced by RANGE MEMBERSHIP, not by equity.
 *
 * Equity before the flop is a bad guide to a preflop decision and always has been:
 * 32o has 32% equity against a random hand and is unplayable, because equity ignores
 * that the hand cannot be played after the flop. So the bot asks the question players
 * actually ask — is this hand in the range I open, call or raise from here — and
 * prices actions by how comfortably it sits inside or outside that range.
 *
 * The one place raw numbers come back is a short stack, where the whole hand IS the
 * all-in and there is no postflop to misjudge.
 */
function pricePreflop(input: PolicyInput, legal: Action[]): PricedAction[] {
  const { hand, seat, profile } = input;
  const me = hand.seats[seat]!;
  const code = comboCode(comboId(me.hole[0]!, me.hole[1]!));
  const bb = hand.rules.bigBlind;
  const stack = me.stack + me.committed;
  const effectiveBb = effectiveStack(hand, seat) / bb;

  const behind = preflopSeatsBehind(hand, seat);
  /**
   * How many raises have gone in, counted from the ACTIONS rather than from who has
   * chips in front of them. The seat-based version could not tell a three-bet from a
   * four-bet — both look like "one opponent has more than a big blind in" — so a
   * four-bet was sized as if it were the first raise and came in at four times the
   * three-bet.
   */
  const raisers = raisesThisStreet(hand);
  const owed = currentBet(hand) - me.committed;

  const pot = hand.potSize();
  /**
   * A short stack plays push/fold, where the hand is one decision and equity is the
   * whole story. The threshold widens as the stack shortens and as the seats behind
   * run out — the two things that actually drive a shove.
   */
  const shortStack = hand.rules.actionSet === "push_fold" ? effectiveBb <= 14 : effectiveBb <= 10;

  const out: PricedAction[] = [];
  for (const action of legal) {
    switch (action.kind) {
      case "fold":
        out.push({ action, ev: 0, label: "fold", note: "folds" });
        break;

      case "check":
        out.push({ action, ev: 0.35 * bb, label: "check", note: "checks the option" });
        break;

      case "call": {
        const cost = Math.min(owed, me.stack);
        const width = raisers > 0
          ? continueWidth(input, behind, effectiveBb).call
          : rangeCodes({
              percent: openPercent(behind) * 1.9, earliness: earlinessOf(behind),
              style: profile.style,
            });
        const depth = rangeDepth(code, width);
        /**
         * The price of a call is scaled by the pot it is buying into and the price
         * being paid, so a bot calls one big blind wide and ten big blinds narrowly.
         * Without the cost term every call looked the same and the bots called shoves
         * with suited connectors.
         */
        /**
         * Open-limping. Calling one blind into an unopened pot from a seat that is not
         * the big blind is not a cheap way into a hand — it forfeits the chance to win
         * it right now and invites everyone behind in. Real strategy is raise or fold;
         * real WEAK players limp constantly, which is why the penalty is scaled by how
         * passive the profile is rather than applied flat.
         *
         * Without it the expert was voluntarily entering 21% of pots and raising only
         * 9% of them, which is not a style, it is a bug that looks like one.
         */
        const isBigBlind = me.committed >= bb && raisers === 0;
        const openLimp = raisers === 0 && !isBigBlind && cost > 0;
        const limpPenalty = openLimp ? -bb * (0.15 + profile.style.aggression * 1.2) : 0;
        const ev = depth * 1.1 * bb * potPull(pot, cost) - cost * 0.06 + limpPenalty;
        out.push({
          action, ev,
          label: cost > 0 ? `call ${fmt(cost / bb)}bb` : "call",
          note: depth > 0 ? `${code} is inside the calling range` : `${code} is outside the calling range`,
        });
        break;
      }

      case "bet":
      case "raise":
      case "allin": {
        const width = raisers > 0
          ? continueWidth(input, behind, effectiveBb).threeBet
          : rangeCodes({ percent: openPercent(behind), earliness: earlinessOf(behind), style: profile.style });
        const depth = rangeDepth(code, width);

        for (const p of preflopSizes(hand, seat, action, raisers)) {
          const isShove = p.to >= stack;
          /**
           * Shoving a deep stack is not the same decision as shoving a short one, and
           * the model has to know it. Short: the all-in is the plan. Deep: it is a way
           * to set money on fire with the second-best hand.
           */
          const shovePenalty = isShove && !shortStack && effectiveBb > 20
            ? -Math.min(14, effectiveBb / 8) * bb : 0;
          /**
           * Aggression multiplies the REWARD for raising a good hand; it does not
           * soften the cost of raising a bad one. Applying it to both was backwards —
           * it made the passive profiles the LEAST reluctant to raise their trash,
           * which is exactly the opposite of what a calling station is.
           */
          /**
           * Aggression multiplies the REWARD for raising a good hand; it does not
           * soften the cost of raising a bad one. Applying it to both was backwards —
           * it made the passive profiles the LEAST reluctant to raise their trash.
           *
           * The reward curve is deliberately steep in aggression. A gentle multiplier
           * left the calling station raising a quarter of the hands it played, because
           * raising a good hand is genuinely correct and a small discount does not
           * change that. A passive player is not someone who slightly prefers calling.
           */
          const drive = 0.25 + Math.pow(profile.style.aggression, 0.8) * 1.55;
          const reward = depth > 0 ? depth * 1.4 * bb * drive : 0;
          const cost = depth > 0 ? 0 : depth * 2.2 * bb * (1.4 - profile.style.aggression * 0.6);
          /**
           * Sizing is a small effect, deliberately. It is priced as a penalty for
           * being far from the standard size, tilted by the profile's own preference —
           * NOT as a reward for betting more, which is what the first version did and
           * why every bot opened for its whole stack with any hand it liked.
           */
          const want = 0.5 + profile.style.sizingBias;
          const sizePull = -Math.abs(p.standardness - want) * 1.1 * bb
            + (depth < 0 ? -(p.standardness) * 0.8 * bb : 0);
          out.push({
            action: p.action, ev: reward + cost + sizePull + shovePenalty,
            label: p.label,
            note: depth > 0
              ? `${code} is in the ${raisers > 0 ? "3-betting" : "opening"} range from here`
              : `${code} is not a hand to raise from here`,
          });
        }
        break;
      }
    }
  }
  return out;
}

/**
 * How comfortably a hand sits inside a range: +1 at the very top, 0 at the boundary,
 * and increasingly negative the further outside it falls.
 *
 * The old version returned a flat -0.5 for everything outside, which made 85o and 72o
 * identically bad — so with the softmax over prices, a bot that raised trash raised
 * ALL trash equally often. Distance past the edge is what separates a loose player
 * from a player who has stopped looking at their cards.
 */
function rangeDepth(code: string, width: readonly string[]): number {
  const rank = handRank(code);
  const edge = worstRank(width);
  if (width.includes(code)) return 1 - rank / Math.max(1, edge);
  return -(0.25 + ((rank - edge) / 168) * 3.5);
}

/**
 * Preflop raise sizes.
 *
 * The rules only distinguish a minimum raise and an all-in, so left to the engine every
 * bot min-raises — which is not a size anybody uses and makes every pot a limp-fest
 * that resolves in a raising war. These are the sizes the game is actually played at:
 * open to a bit over two blinds, three-bet to about three times the open, four-bet to
 * about two and a bit times the three-bet.
 */
function preflopSizes(
  hand: Hand, seat: number, template: Action, raisers: number
): { action: Action; to: number; standardness: number; label: string }[] {
  const me = hand.seats[seat]!;
  const bb = hand.rules.bigBlind;
  const bet = currentBet(hand);
  const maxTo = me.committed + me.stack;
  const minTo = template.to ?? bet + bb;

  if (template.kind === "allin" || hand.rules.actionSet === "push_fold") {
    return [{ action: template, to: maxTo, standardness: 1, label: "all in" }];
  }

  let targets: number[];
  if (raisers === 0) {
    // Opening. Limpers make the pot bigger, so the open grows with them.
    const limpers = hand.seats.filter(
      (s, i) => i !== seat && !s.folded && !s.sittingOut && s.committed >= bb).length - 1;
    targets = [2.2, 2.75, 3.5].map((x) => bb * (x + Math.max(0, limpers)));
  } else if (raisers === 1) {
    // Three-betting: about three times the open, wider when out of position.
    targets = [2.6, 3.0, 3.6].map((x) => bet * x);
  } else {
    // Four-betting and beyond: barely more than double, because the pot is already
    // large and anything more is just a shove with extra steps.
    targets = [2.1, 2.4].map((x) => bet * x);
  }

  const seen = new Set<number>();
  const out: { action: Action; to: number; standardness: number; label: string }[] = [];
  targets.forEach((t, i) => {
    const to = Math.max(minTo, Math.min(maxTo, round(t, hand.rules.chipSize)));
    if (seen.has(to)) return;
    seen.add(to);
    out.push({
      action: { kind: template.kind, to },
      to,
      // 0 for the smallest listed size, 1 for the largest: a scale the profile's
      // sizing preference can be compared against directly.
      standardness: targets.length > 1 ? i / (targets.length - 1) : 0.5,
      label: to >= maxTo ? "all in" : `raise to ${fmt(to / bb)}bb`,
    });
  });
  /**
   * The all-in is NOT appended here.
   *
   * `legalActions` already returns a separate `allin` template, and `pricePreflop`
   * loops over every legal action — so appending it produced two entries for the same
   * move at two different prices (different `standardness`, hence different size
   * penalty). The softmax then summed both, roughly doubling the weight on shoving,
   * worst at short stacks where the two prices are closest; the hand history showed
   * "all in" twice with contradictory numbers; and grading matched the hero's shove
   * against the more favourable of the two, understating what it cost.
   */
  return out;
}


function continueWidth(input: PolicyInput, behind: number, effectiveBb: number) {
  const { hand, seat, profile } = input;
  const raiserSeat = hand.seats.findIndex(
    (s, i) => i !== seat && !s.folded && !s.sittingOut && s.committed > hand.rules.bigBlind);
  const read = raiserSeat >= 0 ? input.reads.get(raiserSeat) : undefined;
  const vsPercent = read?.openedPercent ?? openPercent(Math.max(0, behind + 1));
  return defenceAgainstRaise({
    vsPercent, inPosition: behind === 0, style: profile.style, effectiveBb,
  });
}

/**
 * The rank of the weakest hand in a range — the edge that `rangeDepth` measures from.
 *
 * Not cached. It was, keyed on the range's length and its last code, and that key
 * collides: `rangeCodes` reorders the hand list by position before slicing, so two
 * ranges can share both and hold different hands. A sweep found 49 collisions across
 * the real profiles, some off by three places, which is a fifty-percent swing in the
 * priced value of a call.
 *
 * Worse than the magnitude: the cache was module-global and never cleared, so which
 * value won depended on which table had been opened first in that tab. That breaks
 * the guarantee the whole engine is built on — that a seed replays exactly — and it
 * would have made a hand history disagree with the hand it recorded. A loop over at
 * most 169 strings is not worth that.
 */
function worstRank(codes: readonly string[]): number {
  if (codes.length === 0) return 1;
  let worst = 0;
  for (const c of codes) worst = Math.max(worst, handRank(c));
  return worst;
}

/** Bigger pots pull calls wider, but with diminishing returns. */
function potPull(pot: number, cost: number): number {
  if (cost <= 0) return 1;
  const odds = pot / cost;
  return Math.max(0.35, Math.min(2.2, 0.55 + Math.log10(1 + odds) * 0.85));
}

/* ----------------------------------------------------------------- postflop --- */

function pricePostflop(input: PolicyInput, legal: Action[]): PricedAction[] {
  const { hand, seat, profile } = input;
  const me = hand.seats[seat]!;
  const board = hand.board;
  const pot = hand.potSize();
  const owed = currentBet(hand) - me.committed;
  const bb = hand.rules.bigBlind;

  const opponents = hand.seats
    .map((s, i) => ({ s, i }))
    .filter(({ s, i }) => i !== seat && !s.folded && !s.sittingOut);

  const t = texture(board as Card[]);
  const hc = classify(me.hole, board);
  const trials = input.trials ?? (opponents.length > 2 ? 220 : 400);

  const ranges = opponents.map(({ i }) => {
    const r = input.reads.get(i);
    return r ? perceived(r, profile.style.handReading) : Range.full();
  });
  const eq = safeEquity(me.hole, board, ranges, trials, input.rng);

  const advantage = rangeAdvantage(t);
  const priorRaises = raisesThisStreet(hand);

  /**
   * The least-folding opponent decides whether a bluff works, not the average one.
   * Everybody has to fold for a bluff to win, so one station in the pot is enough to
   * make it a value-only spot.
   */
  const observedFold = (() => {
    if (!input.histories) return null;
    let worst: number | null = null;
    for (const { i } of opponents) {
      const v = foldToBet(input.histories[i] ?? { hands: 0, vpip: 0, pfr: 0, betsFaced: 0, foldsToBet: 0 });
      if (v === null) continue;
      worst = worst === null ? v : Math.min(worst, v);
    }
    return worst;
  })();

  /**
   * Equity against the part of their range that would still be there facing a bet —
   * recomputed FOR EACH SIZE, because the size is what decides how much continues.
   * A single fixed "top 45%" was the second half of the bluffing bug: an all-in was
   * priced against the same range as a third-pot stab, so shoving air looked like the
   * bet that folded out the most while facing the same hands.
   */
  const calledCache = new Map<number, number>();
  const equityWhenCalled = (fe: number): number => {
    const keep = Math.max(0.10, Math.min(0.9, 1 - fe));
    const key = Math.round(keep * 20);
    const hit = calledCache.get(key);
    if (hit !== undefined) return hit;
    const cont = ranges.map((r) => topOfRange(r, me.hole, board, keep, input.rng));
    const v = safeEquity(me.hole, board, cont, Math.max(120, trials >> 1), input.rng).equity;
    calledCache.set(key, v);
    return v;
  };
  const out: PricedAction[] = [];

  for (const action of legal) {
    switch (action.kind) {
      case "fold":
        out.push({ action, ev: 0, label: "fold", note: "folds" });
        break;

      case "check": {
        /**
         * Checking is not worth zero. It keeps the pot and a share of it, and with a
         * draw it buys a free card. Pricing it at zero made every bot bet or fold and
         * never check back, which is not poker.
         */
        /**
         * How much of its equity a checked hand actually realises.
         *
         * Not a constant. A strong hand that checks almost always gets to show it
         * down; air realises nothing, because someone will bet and it will fold. A
         * flat 0.62 undervalued checking with exactly the hands that should check —
         * decent but not good enough to build a pot with — and the bots duly
         * continuation-bet between 83% and 100% of flops against a healthy 45-75%.
         *
         * It falls with the number of opponents, because the more players there are
         * the less often a check reaches a showdown uncontested.
         */
        const realisation = Math.max(0.35, Math.min(0.88, 0.42 + eq.equity * 0.55))
          * (1 - Math.min(0.25, (opponents.length - 1) * 0.09));
        const showdownValue = eq.equity * pot * realisation;
        const freeCard = hc.outs > 0 ? hc.outs * 0.02 * pot : 0;
        out.push({
          action, ev: showdownValue + freeCard,
          label: "check",
          note: describe(hc, eq.equity),
        });
        break;
      }

      case "call": {
        const cost = Math.min(owed, me.stack);
        const raw = eq.equity * (pot + cost) - cost;
        /**
         * Implied odds for a hand that will win a lot more when it gets there, and
         * reverse implied odds for one that will lose more when it does not. A flush
         * draw calling is not the same as a weak pair calling even at identical equity.
         */
        /**
         * Implied odds for a hand that will win more when it gets there, and reverse
         * implied odds for one that will lose more when it does not.
         *
         * The bonus is deliberately small. At 55% of the call it was worth more than
         * most of the pot, and the bots called anything with four cards to a flush —
         * which is precisely the leak the trainer exists to fix, being modelled as
         * correct play. A draw that is not to the nuts gets less, because the times it
         * gets there and still loses are exactly the expensive ones.
         */
        const implied = hc.outs >= 8 && board.length < 5
          ? cost * (hc.nutFlushDraw || hc.openEnded ? 0.22 : 0.14)
          : hc.category === "pair" && hc.pair !== "over" && hc.pair !== "top" ? -cost * 0.30 : 0;
        /**
         * A sticky player pays more than they should — but by a bounded amount. Scaled
         * by the raw price it grew without limit, so against a hundred-blind shove a
         * calling station's stubbornness was worth twenty-six blinds of imaginary EV
         * and it called off with a board pair. Bounded by the pot, it makes them call
         * too wide without making them suicidal.
         */
        const leash = Math.min(cost, pot * 0.5);
        const stick = (profile.style.stickiness - 0.5) * leash * 0.22
          + (profile.style.callDown - 0.5) * leash * 0.25;
        out.push({
          action, ev: raw + implied + stick,
          label: cost > 0 ? `call ${fmt(cost / bb)}bb` : "check",
          note: `${describe(hc, eq.equity)}; needs ${(pctNeeded(pot, cost) * 100).toFixed(0)}% to call`,
        });
        break;
      }

      case "bet":
      case "raise":
      case "allin": {
        for (const p of sizedCandidates(hand, seat, action, pot)) {
          const fe = foldEquity(p.size, pot, opponents.length, t.wetness, advantage, profile,
            priorRaises, observedFold);
          const eqCalled = equityWhenCalled(fe);
          const called = eqCalled * (pot + 2 * p.size) - p.size;
          const bluffTilt = eq.equity < 0.42
            ? (profile.style.bluffiness - 1) * pot * 0.08 * fe : 0;

          /**
           * Betting is not as good as one street of arithmetic makes it look, and the
           * bots were continuation-betting between 83% and 100% of flops against a
           * healthy range of 45-75%, with an aggression factor of 4 to 9 against a
           * healthy 1.2 to 4. Two things the single-street model leaves out:
           *
           * Not folding is not the same as calling. Some of the range that continues
           * RAISES, and a raise costs more than the bet did — it either wins the pot
           * or forces a decision for more money with the worst of it.
           *
           * And a bet that gets called has bought a bigger pot to play with a hand
           * that is behind. With money still behind, the streets that follow are
           * where that costs; the deeper the stacks the more it costs, which is why
           * the term is scaled by what is left rather than being a constant.
           */
          const behind = Math.max(0, me.stack - p.size);
          const raiseRisk = (1 - fe) * 0.14 * p.size * 1.6;
          const played = (1 - fe) * Math.max(0, 0.5 - eqCalled)
            * Math.min(behind, pot * 1.5) * 0.30;
          /**
           * A small pull toward the size this profile likes. Small on purpose: sizing
           * is a style, and letting it outweigh the actual price of the bet would make
           * a profile with a big sizing preference overbet with everything.
           */
          const sizeStyle = -Math.abs(p.size / Math.max(pot, 1) - profile.style.sizingBias)
            * pot * 0.05;
          const ev = fe * pot + (1 - fe) * called + bluffTilt + sizeStyle - raiseRisk - played;
          out.push({
            action: p.action, ev,
            label: p.label,
            note: eq.equity >= 0.55
              ? `${describe(hc, eq.equity)}; ${(eqCalled * 100).toFixed(0)}% against what calls`
              : `${describe(hc, eq.equity)}; folds out about ${(fe * 100).toFixed(0)}%`,
          });
        }
        break;
      }
    }
  }
  return out;
}

/**
 * Bet sizes to consider.
 *
 * The engine offers one legal raise (the minimum) plus all-in, because that is all the
 * RULES distinguish. A bot that only ever min-raises or shoves is unplayable to sit
 * against, so the sizes are expanded here to the fractions of the pot people actually
 * use, and each is clamped back to something legal.
 */
function sizedCandidates(
  hand: Hand, seat: number, template: Action, pot: number
): { action: Action; size: number; label: string }[] {
  const me = hand.seats[seat]!;
  const bb = hand.rules.bigBlind;
  const maxTo = me.committed + me.stack;
  const bet = currentBet(hand);
  const out: { action: Action; size: number; label: string }[] = [];

  if (template.kind === "allin" || hand.rules.actionSet === "push_fold") {
    out.push({ action: template, size: maxTo - me.committed, label: "all in" });
    return out;
  }

  const minTo = template.to ?? bet + bb;
  const seen = new Set<number>();
  for (const frac of CAND_FRACTIONS) {
    // A raise "of 75% pot" means the pot AFTER calling, which is the size everyone
    // quotes and the one that makes the fold-equity arithmetic below correct.
    const call = Math.max(0, bet - me.committed);
    const target = round(me.committed + call + (pot + call) * frac, hand.rules.chipSize);
    const to = Math.max(minTo, Math.min(maxTo, target));
    if (to <= bet && to < maxTo) continue;
    if (seen.has(to)) continue;
    seen.add(to);
    const size = to - me.committed;
    out.push({
      action: { kind: template.kind, to },
      size,
      label: to >= maxTo ? "all in"
        : template.kind === "bet" ? `bet ${(frac * 100).toFixed(0)}% pot` : `raise to ${fmt(to / bb)}bb`,
    });
  }
  // Not appended here either — see the note in `preflopSizes`. The `allin` template
  // from `legalActions` is what prices the shove, exactly once.
  return out;
}

/**
 * How often everyone folds to a bet of this size.
 *
 * Anchored on minimum-defence frequency — against a bet of `b` into a pot of `p`, a
 * player who folds more than b/(p+b) can be bluffed profitably with any two cards —
 * then moved by the things that actually change it: how wet the board is, whose range
 * the board favours, how many players have to fold rather than one, how good this bot
 * is at judging any of it, and how much aggression has already gone in.
 *
 * That last term is not a refinement, it is the fix for a real and very visible bug.
 * Without it the bots got into raising wars with nothing: each re-raise was priced as
 * folding out another 70% of a range that had, by then, already raised twice. Someone
 * who has just raised is not about to fold, and a model that does not know that will
 * bluff its whole stack off on every street it is allowed to.
 */
function foldEquity(
  size: number, pot: number, opponents: number, wetness: number,
  advantage: number, profile: BotProfile, priorRaises: number,
  observed: number | null
): number {
  if (size <= 0 || pot <= 0) return 0;
  const alpha = size / (pot + size);          // MDF: the share that "should" fold
  let one = alpha;
  one *= 1 + advantage * 0.30;                // a board that favours us folds more out
  one *= 1 - wetness * 0.30;                  // a wet board gives everyone something
  // A bot that reads well judges this better; a bot that does not is systematically
  // optimistic about how often people fold, which is exactly how weak players bluff.
  one *= 0.85 + (1 - Math.min(1, profile.style.handReading)) * 0.35;
  // Each aggressive action already in this round makes the next fold much less likely.
  one *= Math.pow(0.5, Math.max(0, priorRaises));
  /**
   * And then: what have these particular opponents ACTUALLY been doing?
   *
   * This is the difference between a bot that plays poker and a bot that plays a
   * model of poker. Without it every profile bluffed at the theoretical rate against
   * a calling station, which is how a table of solid regulars managed to lose three
   * hundred big blinds per hundred hands to the worst player in the game. A player who
   * has folded to one bet in ten is not going to start now.
   *
   * Scaled by hand-reading skill, so a weak bot keeps bluffing into the station and a
   * good one stops. That gap IS the lesson the game is teaching.
   */
  if (observed !== null) {
    const ratio = Math.max(0.25, Math.min(1.9, observed / 0.45));
    one *= 1 + (ratio - 1) * Math.min(1, profile.style.handReading);
  }
  /**
   * Capped well below certainty. An overbet does not fold out 90% of anybody's range,
   * and letting the model believe it does is what turns "I have nothing" into "so I
   * should bet everything".
   */
  one = Math.max(0.02, Math.min(0.72, one));
  return Math.pow(one, Math.max(1, opponents));
}

/** The top slice of a range on this board — what continues facing a bet. */
function topOfRange(
  r: Range, hole: readonly Card[], board: readonly Card[], keep: number, rng: () => number
): Range {
  const ids = r.ids();
  if (ids.length === 0) return r;
  const boardSet = new Set([...hole, ...board]);
  const scored: { id: number; e: number }[] = [];
  for (const id of ids) {
    const [a, b] = comboCards(id);
    if (boardSet.has(a) || boardSet.has(b)) continue;
    scored.push({ id, e: boardStrength(a, b, board) });
  }
  if (scored.length === 0) return r;
  scored.sort((x, y) => y.e - x.e);
  const cut = Math.max(1, Math.floor(scored.length * keep));
  const out = Range.empty();
  for (let i = 0; i < cut; i++) out.w[scored[i]!.id] = r.weight(scored[i]!.id);
  // A little of the tail survives: nobody's continuing range is exactly the top 45%.
  for (let i = cut; i < scored.length; i++) {
    if (rng() < 0.12) out.w[scored[i]!.id] = r.weight(scored[i]!.id) * 0.4;
  }
  return out;
}

/**
 * Equity that never throws into the middle of a hand.
 *
 * A range narrowed past the point where the board leaves it any combos is a lost read,
 * not a reason to crash a game the player is in the middle of. Falling back to a full
 * range is the conservative answer: the bot forgets what it thought it knew.
 */
function safeEquity(
  hole: readonly Card[], board: readonly Card[], ranges: Range[], trials: number, rng: () => number
) {
  const seed = Math.floor(rng() * 0x7fffffff) || 1;
  try {
    return equityVsRanges(hole, board, ranges, trials, seed);
  } catch {
    return equityVsRanges(hole, board, ranges.map(() => Range.full()), trials, seed);
  }
}

/* -------------------------------------------------------------------- bits --- */

/**
 * Aggressive actions already taken on a betting round.
 *
 * The street is a PARAMETER rather than being read from the hand, because callers that
 * run after an action has been applied are asking about the street the action was on,
 * not the one the hand has since moved to. Reading `hand.street` there reported zero
 * raises for the last action of every raised preflop round — which is how the
 * fold-to-bet counter came out two short of the truth.
 */
export function raisesOnStreet(hand: Hand, street: Street): number {
  let n = 0;
  for (const e of hand.history()) {
    if (e.street !== street) continue;
    if (e.action.kind === "bet" || e.action.kind === "raise" || e.action.kind === "allin") n++;
  }
  return n;
}

/** Aggressive actions already taken on the round the hand is on now. */
export function raisesThisStreet(hand: Hand): number {
  return raisesOnStreet(hand, hand.street);
}

export function currentBet(hand: Hand): number {
  let max = 0;
  for (const s of hand.seats) if (!s.sittingOut && s.committed > max) max = s.committed;
  return max;
}

export function effectiveStack(hand: Hand, seat: number): number {
  const me = hand.seats[seat]!;
  const mine = me.stack + me.committed;
  let biggestOther = 0;
  hand.seats.forEach((s, i) => {
    if (i === seat || s.folded || s.sittingOut) return;
    biggestOther = Math.max(biggestOther, s.stack + s.committed);
  });
  return Math.min(mine, biggestOther || mine);
}

/**
 * How many seats act after this one BEFORE THE FLOP — the definition of position.
 *
 * Counted from the seating, walking clockwise to the big blind, and NOT from who is
 * still in the pot. The previous version counted every live seat at the table, which
 * had two separate consequences and both of them were bad: at the moment reads are
 * seeded nobody has folded yet, so every seat looked equally early and the positional
 * model did nothing at all; and once a hand was underway, a limper counted as a seat
 * "behind" the button, so the button tightened by nine points in exactly the spot
 * where it should widen.
 */
export function preflopSeatsBehind(hand: Hand, seat: number): number {
  const n = hand.seats.length;
  const live = hand.seats.map((s, i) => (s.sittingOut ? -1 : i)).filter((i) => i >= 0);
  if (live.length <= 1) return 0;

  // The big blind is the last seat to act preflop, so counting stops there.
  const after = (from: number): number => {
    for (let step = 1; step <= n; step++) {
      const k = (from + step) % n;
      if (!hand.seats[k]!.sittingOut) return k;
    }
    return from;
  };
  const bb = live.length === 2 ? after(hand.button) : after(after(hand.button));

  let count = 0;
  let k = seat;
  for (let step = 0; step < n; step++) {
    if (k === bb) break;
    k = after(k);
    count++;
  }
  return count;
}

/** Seats still to act after this one on the current betting round. */
export function seatsToActAfter(hand: Hand, seat: number): number {
  const n = hand.seats.length;
  let count = 0;
  for (let step = 1; step < n; step++) {
    const k = (seat + step) % n;
    const s = hand.seats[k]!;
    if (s.sittingOut || s.folded || s.allIn) continue;
    count++;
  }
  return count;
}

function earlinessOf(behind: number): number { return Math.min(1, behind / 8); }

function pctNeeded(pot: number, cost: number): number {
  return cost <= 0 ? 0 : cost / (pot + cost);
}

function round(x: number, chip: number): number {
  return chip > 0 ? Math.round(x / chip) * chip : x;
}

function fmt(x: number): string {
  return Math.abs(x - Math.round(x)) < 0.005 ? String(Math.round(x)) : x.toFixed(1);
}

function describe(hc: ReturnType<typeof classify>, equity: number): string {
  const name = hc.category === "air"
    ? (hc.flushDraw ? "a flush draw" : hc.openEnded ? "an open-ended draw"
      : hc.gutshot ? "a gutshot" : hc.overcards > 0 ? `${hc.overcards} overcard${hc.overcards > 1 ? "s" : ""}`
      : "nothing")
    : hc.category === "pair" ? `${hc.pair} pair`
    : hc.category.replace(/_/g, " ");
  return `${name} at ${(equity * 100).toFixed(0)}%`;
}
